import React from "react";
import clsx from "clsx";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import enumParser from "utils/enum-parser";
import { getPersonFullName } from "utils/person";
import PositionAtPoint from "./Parts/PositionAtPoint";
import RaceDistance from "./Parts/RaceDistance";

import useStyles from "./styles";

interface IProps {
  raceHistory: racehorse360.IRaceHistory;
}

const CodedPPS = (props: IProps) => {
  const { raceHistory } = props;
  const classes = useStyles();

  const raceNameAbbr = [
    raceHistory.race?.raceType,
    raceHistory.race?.raceNameAbbreviated,
    raceHistory.race?.raceConditions?.replace(/\s+/, "")
  ]
    .filter(Boolean)
    .join("-");

  const renderFractions = (rh: racehorse360.IRaceHistory) => {
    return new Array(5).fill("").reduce((acc, cur, i) => {
      if (rh.race && rh.race[`fraction_${i + 1}`]) {
        const timingReversed = String(rh.race[`fraction_${i + 1}`])
          .split("")
          .reverse();

        const timing = Array.from(timingReversed, (char, index) => {
          if (index % 2) {
            return ":" + char;
          } else {
            return char;
          }
        })
          .reverse()
          .join("");
        return `${acc} ${timing} `;
      }
      return acc;
    }, "");
  };

  const renderCourseSurface = courseType => {
    if (
      [
        racehorse360.CourseSurface.COURSE_SURFACE_TURF,
        racehorse360.CourseSurface.COURSE_SURFACE_STEEPLECHASE,
        racehorse360.CourseSurface.COURSE_SURFACE_OUTER_TURF,
        racehorse360.CourseSurface.COURSE_SURFACE_JUMP,
        racehorse360.CourseSurface.COURSE_SURFACE_HUNT,
        racehorse360.CourseSurface.COURSE_SURFACE_DOWNHILL_TURF,
        racehorse360.CourseSurface.COURSE_SURFACE_HURDLE
      ].includes(courseType)
    ) {
      return (
        <>
          <span className={classes.circleBorder}>T</span>{" "}
        </>
      );
    }
    if (
      courseType === racehorse360.CourseSurface.COURSE_SURFACE_ALL_WEATHER_TRACK
    ) {
      return (
        <>
          <span className={classes.circleBorder}>A</span>{" "}
        </>
      );
    }
    if (courseType === racehorse360.CourseSurface.COURSE_SURFACE_INNER_TURF) {
      return (
        <>
          <span className={classes.rectBorder}>T</span>{" "}
        </>
      );
    }
    if (courseType === racehorse360.CourseSurface.COURSE_SURFACE_INNER_TRACK) {
      return (
        <>
          <span className={classes.circleBorder}>
            <span className={classes.dot} />
          </span>{" "}
        </>
      );
    }
    return <></>;
  };

  const renderPOCs = (rh: racehorse360.IRaceHistory) => {
    const pocs = [];
    new Array(5).fill("").forEach((item, i) => {
      const position = rh[`positionAtPointOfCall_${i + 1}`] as number;
      const lengthAhead = rh[`lengthAheadAtPointOfCall_${i + 1}`] as number;
      const lengthBehind = rh[`lengthBehindAtPointOfCall_${i + 1}`] as number;

      if (position) {
        pocs.push(
          <React.Fragment key={i}>
            <PositionAtPoint
              position={position}
              lengthAhead={lengthAhead}
              lengthBehind={lengthBehind}
            />{" "}
          </React.Fragment>
        );
      }
    });

    return <>{pocs}</>;
  };

  const renderOdds = (odds: number = 0) => {
    const formatted = (odds / 100).toLocaleString("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    });
    return odds >= 100 ? formatted : formatted.substring(1);
  };

  return (
    <>
      <p className={classes.perfText}>
        <span>
          {format(parseISO(raceHistory.date), "dMMMyy")}-
          {raceHistory.raceNumber}
          {raceHistory.facility?.code}{" "}
          {enumParser.parseTrackCondition(raceHistory.race?.trackCondition)}{" "}
          <RaceDistance
            distance={raceHistory.race?.distance}
            unit={raceHistory.race?.distanceUnit}
            showAboutDistanceIndicator={Boolean(
              raceHistory.race?.aboutDistanceIndicator
            )}
          />{" "}
        </span>
        {renderCourseSurface(raceHistory.race?.courseType)}
        {!raceHistory.isScratched && renderFractions(raceHistory)}
        {raceHistory.race?.genderRestriction &&
          raceHistory.race?.genderRestriction === "B" && (
            <>
              <span className={classes.circleBorder}>F</span>{" "}
            </>
          )}
        {raceHistory.race?.ageRestriction?.replace("0", "")} {raceNameAbbr}{" "}
        {!raceHistory.isScratched && raceHistory.race?.raceType !== "CAN" && (
          <>
            {renderPOCs(raceHistory)}
            <PositionAtPoint
              position={raceHistory.positionAtFinish}
              lengthAhead={raceHistory.lengthAheadAtFinish}
              lengthBehind={raceHistory.lengthBehindAtFinish}
            />{" "}
            {Boolean(raceHistory.jockey) && (
              <>
                {raceHistory.jockey?.lastName}{" "}
                {raceHistory.jockey?.firstName?.charAt(0)}{" "}
              </>
            )}
            {raceHistory.weightCarried} {raceHistory.medication?.trim()}
            {raceHistory.equipment?.toLowerCase()}{" "}
            {renderOdds(raceHistory.odds)} {raceHistory.postPosition}/
            {raceHistory.race?.numberOfRunners}
          </>
        )}
      </p>
      {raceHistory.isScratched && (
        <p className={clsx(classes.perfText, classes.alert)}>
          SCRATCHED
          {raceHistory.scratchReason ? (
            <>&nbsp;-&nbsp;{raceHistory.scratchReason}</>
          ) : (
            ""
          )}
        </p>
      )}
      {raceHistory.race?.raceType === "CAN" && (
        <p className={clsx(classes.perfText, classes.alert)}>
          RACE CANCELED
          {raceHistory.scratchReason ? (
            <>&nbsp;-&nbsp;{raceHistory.scratchReason}</>
          ) : (
            ""
          )}
        </p>
      )}
      <span className={classes.perfComment}>{raceHistory.comment}</span>
      {Boolean(raceHistory.newOwner) && !raceHistory.isVoid?.value && (
        <p className={classes.perfText}>
          Claimed by {getPersonFullName(raceHistory.newOwner)}, Trainer:{" "}
          {getPersonFullName(raceHistory.newTrainer)}
        </p>
      )}
      {raceHistory.isVoid?.value && (
        <p className={clsx(classes.perfText, classes.alert)}>
          Claim voided, Reason:{" "}
          {enumParser.parseVoidReason(raceHistory.voidReason)}
        </p>
      )}
    </>
  );
};

export default React.memo(CodedPPS);
